import Flag from '../Flag/Flag';
import './SymbolFlag.scss';

/** ISO country per currency, for splitting a pair like GBPJPY into GB + JP. */
const CURRENCY = {
  USD: 'US', EUR: 'EU', GBP: 'GB', JPY: 'JP', AUD: 'AU', NZD: 'NZ', CAD: 'CA',
  CHF: 'CH', SEK: 'SE', NOK: 'NO', DKK: 'DK', PLN: 'PL', CZK: 'CZ', HUF: 'HU',
  TRY: 'TR', ZAR: 'ZA', MXN: 'MX', SGD: 'SG', HKD: 'HK', CNH: 'CN', CNY: 'CN',
  INR: 'IN', BRL: 'BR', KRW: 'KR', THB: 'TH', ILS: 'IL',
};

/** Metals and crypto have no country — the design shows a lettered badge. */
const BADGE = { XAU: 'AU', XAG: 'AG', XPT: 'PT', XPD: 'PD', BTC: '₿', ETH: 'Ξ' };
/** Metals additionally get the diamond glyph behind the corner badge — 1:8869. */
const METAL = new Set(['XAU', 'XAG', 'XPT', 'XPD']);

/** Indices and futures resolve to a single flag. */
const SINGLE = {
  SPX500: 'US', NAS100: 'US', US30: 'US', US500: 'US', RUS2000: 'US',
  MGC: 'US', MNQ: 'US', MES: 'US',
  GER40: 'DE', DAX: 'DE', UK100: 'GB', JP225: 'JP', AUS200: 'AU',
  FRA40: 'FR', EU50: 'EU', HK50: 'HK',
  // Energy is dollar-priced, so it takes the US flag whichever benchmark it
  // is — the trading platform shows UKOIL with a US flag too.
  USOIL: 'US', UKOIL: 'US', WTI: 'US', BRENT: 'US', XTIUSD: 'US', XBRUSD: 'US',
  NGAS: 'US', NATGAS: 'US', XNGUSD: 'US', CL: 'US', MCL: 'US', BRN: 'US', NG: 'US',
};

export function resolveSymbol(symbol = '') {
  // "USOIL.PRO" is the broker's symbol; the lookups want "USOIL".
  const s = String(symbol).split('.')[0].toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (SINGLE[s]) return { codes: [SINGLE[s]] };

  const base = s.slice(0, 3);
  const quote = s.slice(3, 6);
  // A metal/crypto base has no natural country of its own — falling back to
  // the quote currency's flag (e.g. a US flag for XAUUSD) was never right,
  // so these always render badge-only, metals additionally get the diamond.
  if (BADGE[base]) return { badge: BADGE[base], metal: METAL.has(base), asset: base.toLowerCase() };
  if (CURRENCY[base] && CURRENCY[quote]) return { codes: [CURRENCY[base], CURRENCY[quote]] };
  if (CURRENCY[base]) return { codes: [CURRENCY[base]] };
  return { badge: s.slice(0, 2) };
}

/**
 * Flag badge beside a trading symbol — 1:8139 / 1:8142.
 * A 22px tile: currency pairs show two offset discs, indices one centred disc,
 * metals a lettered badge.
 */
export default function SymbolFlag({ symbol, size = 22, className = '' }) {
  const { codes = [], badge, metal, asset } = resolveSymbol(symbol);
  const pair = codes.length > 1;
  // `is-badge` / `is-<asset>` are hooks for a page whose design tints these tiles.
  const variant = [pair && 'is-pair', badge && 'is-badge', asset && `is-${asset}`]
    .filter(Boolean)
    .map((c) => ` ${c}`)
    .join('');

  return (
    <span
      className={`sym-flag${variant} ${className}`.trim()}
      style={{ '--tile': `${size}px` }}
      aria-hidden="true"
    >
      {metal && <span className="sym-flag__metal" style={{ '--icon': 'url(/icons/symbol-metal.svg)' }} />}
      {badge && <span className={`sym-flag__badge${metal ? ' is-corner' : ''}`}>{badge}</span>}

      {codes.map((code, i) => (
        <span key={code + i} className={`sym-flag__disc is-${pair ? (i === 0 ? 'back' : 'front') : 'solo'}`}>
          <Flag code={code} fallback={<span className="sym-flag__fallback" />} />
        </span>
      ))}
    </span>
  );
}
